import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import {
    ChatMessage,
    ChatUser,
    Conversation,
} from 'app/modules/admin/chat/chat.types';

/**
 * ChatService
 *  - REST: load conversations / users / messages, create groups & DMs
 *  - SignalR (hub `/chathub`): live messages, online presence, typing
 */
@Injectable({ providedIn: 'root' })
export class ChatService implements OnDestroy {
    private _hub?: HubConnection;
    private _started = false;

    private _conversations$ = new BehaviorSubject<Conversation[]>([]);
    private _users$ = new BehaviorSubject<ChatUser[]>([]);
    private _onlineIds$ = new BehaviorSubject<Set<number>>(new Set());
    private _activeConversation$ = new BehaviorSubject<Conversation | null>(null);
    private _messages$ = new BehaviorSubject<ChatMessage[]>([]);

    /** Fired whenever a new message arrives over SignalR. */
    private _messageReceived$ = new Subject<ChatMessage>();

    constructor(private _http: HttpClient) {
        // Best-effort: gracefully close the SignalR connection when the tab
        // or browser is closing so the server fires OnDisconnectedAsync
        // and broadcasts `UserOffline` to other users.
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                try {
                    if (this._hub) {
                        this._hub.stop();
                    }
                } catch { /* best effort only */ }
            });
        }
    }

    // ----- public observables -----
    get conversations$(): Observable<Conversation[]> { return this._conversations$.asObservable(); }
    get users$(): Observable<ChatUser[]> { return this._users$.asObservable(); }
    get onlineIds$(): Observable<Set<number>> { return this._onlineIds$.asObservable(); }
    get activeConversation$(): Observable<Conversation | null> { return this._activeConversation$.asObservable(); }
    get messages$(): Observable<ChatMessage[]> { return this._messages$.asObservable(); }
    get messageReceived$(): Observable<ChatMessage> { return this._messageReceived$.asObservable(); }

    // ----- REST -----
    private apiBase(): string {
        return environment.baseUrl; // e.g. https://localhost:5006/api
    }

    /** root url for SignalR (/api stripped). */
    private hubBase(): string {
        const base = this.apiBase();
        return base.replace(/\/api\/?$/, '');
    }

    /**
     * SQL Server `datetime` columns come back from the API without a timezone
     * marker, so the browser interprets them as local time and shifts the
     * displayed clock by the user's offset. The backend always *stores* UTC
     * (everything uses `DateTime.UtcNow`), so we just append "Z" when missing
     * to make the browser parse it as UTC and render in local time correctly.
     */
    private toUtcIso(s: any): any {
        if (!s) return s;
        const str = String(s);
        return /Z$|[+\-]\d{2}:?\d{2}$/.test(str) ? str : str + 'Z';
    }

    private normalizeMessage<T extends ChatMessage>(m: T): T {
        return { ...m, createdOn: this.toUtcIso(m.createdOn) };
    }

    private normalizeConversation(c: Conversation): Conversation {
        return {
            ...c,
            lastMessageAt: this.toUtcIso(c.lastMessageAt),
            lastMessage: c.lastMessage ? this.normalizeMessage(c.lastMessage) : c.lastMessage,
        };
    }

    loadConversations(): Observable<Conversation[]> {
        const obs = this._http.get<Conversation[]>(`${this.apiBase()}/Chat/conversations`);
        obs.subscribe((list) =>
            this._conversations$.next((list || []).map((c) => this.normalizeConversation(c)))
        );
        return obs;
    }

    loadUsers(): Observable<ChatUser[]> {
        const obs = this._http.get<ChatUser[]>(`${this.apiBase()}/Chat/users`);
        obs.subscribe((list) => this._users$.next(list ?? []));
        return obs;
    }

    loadMessages(conversationId: number, skip = 0, take = 50): Observable<ChatMessage[]> {
        const obs = this._http.get<ChatMessage[]>(
            `${this.apiBase()}/Chat/conversations/${conversationId}/messages?skip=${skip}&take=${take}`
        );
        obs.subscribe((list) =>
            this._messages$.next((list || []).map((m) => this.normalizeMessage(m)))
        );
        return obs;
    }

    createGroup(name: string, memberIds: number[], description?: string): Observable<any> {
        return this._http.post(`${this.apiBase()}/Chat/groups`, {
            name,
            description,
            memberIds,
        });
    }

    getOrCreateDirect(otherUserId: number): Observable<any> {
        return this._http.post(`${this.apiBase()}/Chat/conversations/direct/${otherUserId}`, {});
    }

    addMember(groupId: number, userId: number): Observable<any> {
        return this._http.post(`${this.apiBase()}/Chat/groups/${groupId}/members/${userId}`, {});
    }

    removeMember(groupId: number, userId: number): Observable<any> {
        return this._http.delete(`${this.apiBase()}/Chat/groups/${groupId}/members/${userId}`);
    }

    updateGroup(groupId: number, name?: string, description?: string): Observable<any> {
        return this._http.put(`${this.apiBase()}/Chat/groups/${groupId}`, { name, description });
    }

    markRead(conversationId: number): Observable<any> {
        return this._http.post(`${this.apiBase()}/Chat/conversations/${conversationId}/read`, {});
    }

    /**
     * Set the conversation by id (used by the router resolver pattern).
     * If not yet in the conversations cache it triggers a refresh first.
     */
    selectById(id: number): void {
        const list = this._conversations$.value || [];
        const conv = list.find((c) => c.id === id);
        if (conv) {
            this.setActive(conv);
        } else {
            this.loadConversations().subscribe(() => {
                const c2 = (this._conversations$.value || []).find((c) => c.id === id);
                if (c2) this.setActive(c2);
            });
        }
    }

    resetActive(): void {
        this._activeConversation$.next(null);
        this._messages$.next([]);
    }

    setActive(c: Conversation | null): void {
        this._activeConversation$.next(c);
        if (c) {
            this.loadMessages(c.id).subscribe();
            this.markRead(c.id).subscribe();
        } else {
            this._messages$.next([]);
        }
    }

    // ----- SignalR -----
    /**
     * Idempotent + concurrent-safe.
     *
     * Multiple components (ChatComponent, QuickChatComponent, …) call this on
     * init — without the in-flight promise lock, two concurrent calls would
     * race past the "Connected" guard while the first hub is still
     * "Connecting", create a SECOND hub, and register a SECOND `ReceiveMessage`
     * handler. The server then delivers every message to both connections in
     * the user's `user-{id}` group → the message appears twice on the sender's
     * screen.
     */
    private _startPromise?: Promise<void>;

    start(getToken: () => string): Promise<void> {
        // Already up
        if (this._hub?.state === HubConnectionState.Connected) {
            return Promise.resolve();
        }
        // A start is in flight — every concurrent caller waits on the same one.
        if (this._startPromise) {
            return this._startPromise;
        }
        this._startPromise = this._doStart(getToken).finally(() => {
            this._startPromise = undefined;
        });
        return this._startPromise;
    }

    private async _doStart(getToken: () => string): Promise<void> {
        // If a previous hub instance is still around (e.g. disconnected),
        // dispose it before creating a new one — never leak handlers.
        if (this._hub) {
            try { await this._hub.stop(); } catch { /* no-op */ }
            this._hub = undefined;
        }

        const url = `${this.hubBase()}/chathub`;

        this._hub = new HubConnectionBuilder()
            .withUrl(url, { accessTokenFactory: () => getToken() })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();

        // Live message
        this._hub.on('ReceiveMessage', (m: ChatMessage) => this.handleIncoming(m));

        // Presence
        this._hub.on('OnlineUsers', (ids: number[]) => {
            this._onlineIds$.next(new Set(ids ?? []));
            this.applyPresenceToUsers();
        });
        this._hub.on('UserOnline', (id: number) => {
            const s = new Set(this._onlineIds$.value);
            s.add(id);
            this._onlineIds$.next(s);
            this.applyPresenceToUsers();
        });
        this._hub.on('UserOffline', (id: number) => {
            const s = new Set(this._onlineIds$.value);
            s.delete(id);
            this._onlineIds$.next(s);
            this.applyPresenceToUsers();
        });

        // New / added group
        this._hub.on('GroupCreated', () => {
            this.loadConversations().subscribe();
        });

        try {
            await this._hub.start();
            this._started = true;
        } catch (err) {
            console.error('[ChatService] hub start failed', err);
            this._started = false;
        }
    }

    async stop(): Promise<void> {
        if (this._hub) {
            try { await this._hub.stop(); } catch { /* no-op */ }
            this._hub = undefined;
        }
        this._started = false;

        // Reset local state — when the user signs out we shouldn't keep
        // their conversations / presence around in memory.
        this._conversations$.next([]);
        this._users$.next([]);
        this._onlineIds$.next(new Set());
        this._activeConversation$.next(null);
        this._messages$.next([]);
    }

    sendMessage(conversationId: number, content: string): Promise<void> {
        if (!this._hub || this._hub.state !== HubConnectionState.Connected) {
            return Promise.reject('Hub not connected');
        }
        return this._hub.invoke('SendMessage', conversationId, content, 'text', null);
    }

    typing(conversationId: number): void {
        if (this._hub?.state === HubConnectionState.Connected) {
            this._hub.invoke('Typing', conversationId).catch(() => {});
        }
    }

    // ----- internals -----
    private handleIncoming(raw: ChatMessage): void {
        // Normalize timestamp so the browser renders local time correctly.
        const m = this.normalizeMessage(raw);
        this._messageReceived$.next(m);

        // Append to current conversation messages list (de-dup by id).
        const active = this._activeConversation$.value;
        if (active && active.id === m.chatGroupId) {
            const existing = this._messages$.value;
            const alreadyThere = existing.some((x) => x.id === m.id);
            if (!alreadyThere) {
                this._messages$.next([...existing, m]);
                this.markRead(m.chatGroupId).subscribe();
            }
        }

        const current = this._conversations$.value || [];
        const exists = current.some((c) => c.id === m.chatGroupId);

        // If the conversation isn't in our local cache yet (brand-new chat
        // someone just started with us), refresh from the server so it shows up.
        if (!exists) {
            this.loadConversations().subscribe();
            return;
        }

        // Update conversation list (last message + unread)
        const list = current.map((c) => {
            if (c.id !== m.chatGroupId) return c;
            const isMineActive = active?.id === c.id;
            return {
                ...c,
                lastMessage: m,
                lastMessageAt: m.createdOn,
                unreadCount: isMineActive ? 0 : (c.unreadCount || 0) + 1,
            } as Conversation;
        });
        list.sort((a, b) => +new Date(b.lastMessageAt) - +new Date(a.lastMessageAt));
        this._conversations$.next(list);
    }

    private applyPresenceToUsers(): void {
        const ids = this._onlineIds$.value;
        const users = this._users$.value.map((u) => ({ ...u, isOnline: ids.has(u.id) }));
        this._users$.next(users);
    }

    ngOnDestroy(): void {
        this.stop();
    }
}
